/**
 * RunnerDetailV2 Component
 * Full-page runner detail with dark hero + light timing table
 * Route: /v2/:slug/runner/:runnerId
 *
 * Desktop: Pencil `F5zb0`
 * Mobile: Pencil `MzXAH`
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useRaceData } from '../../hooks/useRaceData';
import { useFollowedRunners } from '../../hooks/useFollowedRunners';
import { fetchRunnerDetail } from '../../lib/queries';
import type { RunnerDetail } from '../../lib/types';

import { RaceHeaderV2 } from './RaceHeaderV2';
import { RunnerAvatarV2 } from './RunnerAvatarV2';
import { StatusBadgeV2 } from './StatusBadgeV2';
import { FollowStarV2 } from './FollowStarV2';
import { ElevationProfileV2 } from './ElevationProfileV2';
import { TimingTableV2 } from './TimingTableV2';
import { SmsSubscribeButtonV2 } from './SmsSubscribeButtonV2';

export function RunnerDetailPage() {
  const { slug, runnerId } = useParams<{ slug: string; runnerId: string }>();
  const navigate = useNavigate();

  const { race, stations, loading: raceLoading, error: raceError } = useRaceData(slug || '');
  const { isFollowed, toggleFollow } = useFollowedRunners(slug || '');

  const [detail, setDetail] = useState<RunnerDetail | null>(null);
  const [loading, setLoading] = useState(false);

  // Load runner detail
  // Depends on race?.id instead of race to avoid refetching on metadata updates
  // (useRaceData creates new race object on every Realtime update)
  useEffect(() => {
    if (!runnerId) {
      setLoading(false);
      setDetail(null);
      return;
    }

    if (!race) {
      // Keep this page in a loading state until race data is available.
      // Without this, there is a brief frame where loading=false and detail=null,
      // which causes a "Runner Not Found" flash before the detail fetch starts.
      setLoading(true);
      setDetail(null);
      return;
    }

    setLoading(true);
    setDetail(null);

    fetchRunnerDetail(runnerId, race.id)
      .then((data) => {
        setDetail(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching runner detail:', err);
        setDetail(null);
        setLoading(false);
      });
  }, [runnerId, race?.id]);

  // Calculate runner's distance on course
  const runnerDistance = useMemo(() => {
    if (!detail || !detail.split_times.length) return undefined;
    const lastPassed = [...detail.split_times]
      .filter(s => s.time)
      .pop();
    return lastPassed?.station_mileage;
  }, [detail]);

  if (raceLoading || (!raceError && loading)) {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: 'var(--v2-text-secondary)' }}>Loading runner details...</div>
      </div>
    );
  }

  if (raceError) {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>
            Race Not Found
          </h1>
          <p style={{ color: 'var(--v2-text-secondary)' }}>
            The race "{slug}" could not be found.
          </p>
        </div>
      </div>
    );
  }

  if (!race || !detail) {
    return (
      <div className="v2-tarawera" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--v2-text-primary)', marginBottom: 8 }}>
            Runner Not Found
          </h1>
          <button
            className="v2-detail-back"
            onClick={() => navigate(`/v2/${slug}`)}
            style={{ margin: '0 auto' }}
          >
            Back to Race
          </button>
        </div>
      </div>
    );
  }

  // Calculate finish elapsed time
  let finishElapsed: string | null = null;
  if (detail.finish_time && detail.race_start_time) {
    const startMs = new Date(detail.race_start_time).getTime();
    const finishMs = new Date(detail.finish_time).getTime();
    const elapsed = finishMs - startMs;
    const hours = Math.floor(elapsed / (1000 * 60 * 60));
    const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((elapsed % (1000 * 60)) / 1000);
    finishElapsed = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  return (
    <div className="v2-tarawera">
      {/* Nav bar */}
      <RaceHeaderV2 race={race} lastUpdated={null} />

      {/* Dark hero section */}
      <div className="v2-detail-hero">
        {/* Back link */}
        <Link to={`/v2/${slug}`} className="v2-detail-back">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Race
        </Link>

        {/* Runner header */}
        <div className="v2-detail-header">
          <RunnerAvatarV2
            firstName={detail.first_name}
            lastName={detail.last_name}
            size="lg"
          />
          <div style={{ flex: 1 }}>
            <div className="v2-detail-name">
              {detail.first_name} {detail.last_name}
            </div>
            <div className="v2-detail-meta">
              <span>BIB {detail.bib}</span>
              {detail.gender && <span>{detail.gender}</span>}
              <span>{race.name}</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <StatusBadgeV2 status={detail.status} label={detail.status_short_label} />
            <FollowStarV2
              isFollowed={isFollowed(detail.bib)}
              onToggle={() => toggleFollow(detail.bib)}
            />
          </div>
        </div>

        {/* Ranks */}
        <div className="v2-detail-ranks">
          {detail.overall_place && (
            <div className="v2-detail-rank">
              <span className="v2-detail-rank-value">{detail.overall_place}</span>
              <span className="v2-detail-rank-label">OVERALL</span>
            </div>
          )}
          {detail.gender_place && (
            <div className="v2-detail-rank">
              <span className="v2-detail-rank-value">{detail.gender_place}</span>
              <span className="v2-detail-rank-label">{detail.gender?.toUpperCase() || 'GENDER'}</span>
            </div>
          )}
          {finishElapsed && (
            <div className="v2-detail-rank">
              <span className="v2-detail-rank-value" style={{ fontSize: 16, fontFamily: '"JetBrains Mono", monospace' }}>
                {finishElapsed}
              </span>
              <span className="v2-detail-rank-label">FINISH TIME</span>
            </div>
          )}
        </div>

        {/* Mini elevation profile with runner position */}
        <ElevationProfileV2
          data={race?.elevation_profile ?? null}
          stations={stations}
          runnerDistance={runnerDistance}
          mini
        />

        {runnerDistance != null && (
          <div className="v2-detail-progress">
            {runnerDistance.toFixed(1)} Mi completed
          </div>
        )}
      </div>

      {/* SMS subscribe CTA — sits between elevation/hero and the timing table */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 24px', background: 'var(--v2-bg-page)' }}>
        <SmsSubscribeButtonV2
          slug={slug || ''}
          bib={detail.bib}
          runnerName={`${detail.first_name} ${detail.last_name}`}
        />
      </div>

      {/* Timing Points table (light section) */}
      <TimingTableV2
        splitTimes={detail.split_times}
        stations={stations}
        raceTimeZone={detail.race_time_zone}
        raceStartTime={detail.race_start_time}
      />
    </div>
  );
}
